import { lazy, Suspense, type ReactNode } from 'react';
import { Flower2, Loader2 } from 'lucide-react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import App from './App';
import { HomeEnhancements } from './HomeEnhancements';
import { ScanSourceChooser } from './ScanSourceChooser';
import { RelatedPlantLinks } from './SeoEnhancements';
import SiteMenu from './SiteMenu';
import { PlantCorrectionWidgetLite, PlantSafetyNoticeLite, SiteFooter } from './SiteChrome';

async function loadProductionPages() {
  await import('./mobile-production.css');
  return import('./ProductionPages');
}

async function loadProductionAccountPages() {
  await import('./mobile-production.css');
  return import('./ProductionAccountPages');
}

const Community = lazy(async () => {
  await Promise.all([
    import('./community.css'),
    import('./community-lightbox.css'),
    import('./community-production.css'),
    import('./mobile-production.css'),
  ]);
  return import('./Community');
});
const PlantLibrary = lazy(() => import('./PlantLibrary'));
const PublicPlantGuide = lazy(() => import('./PublicPlantGuide'));
const PropagationLibrary = lazy(() => import('./PropagationPages').then((module) => ({ default: module.PropagationLibrary })));
const PropagationGuidePage = lazy(() => import('./PropagationPages').then((module) => ({ default: module.PropagationGuidePage })));
const PricingPage = lazy(() => loadProductionPages().then((module) => ({ default: module.PricingPage })));
const PrivacyPage = lazy(() => loadProductionPages().then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => loadProductionPages().then((module) => ({ default: module.TermsPage })));
const CommunityGuidelinesPage = lazy(() => loadProductionPages().then((module) => ({ default: module.CommunityGuidelinesPage })));
const AdminPage = lazy(() => loadProductionPages().then((module) => ({ default: module.AdminPage })));
const AccountPageV2 = lazy(() => loadProductionAccountPages().then((module) => ({ default: module.AccountPageV2 })));
const MyPlantsProductionV2 = lazy(() => loadProductionAccountPages().then((module) => ({ default: module.MyPlantsProductionV2 })));

function RouteLoader() {
  return <main className="narrow empty-state page-loader" aria-live="polite"><Loader2 className="spin" size={28} /><p>Opening PlantGrow...</p></main>;
}

function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoader />}>{children}</Suspense>;
}

function WithScanChooser({ children }: { children: ReactNode }) {
  return <>{children}<ScanSourceChooser /></>;
}

function PageFlow({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <div key={location.pathname} className="page-flow">{children}</div>;
}

function WithSiteMenu({ children }: { children: ReactNode }) {
  return <><SiteMenu /><PageFlow>{children}</PageFlow></>;
}

export default function Root() {
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const publicPlantGuide = path.startsWith('/plants/');

  if (path === '/community') return <WithSiteMenu><WithScanChooser><LazyRoute><Community /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/plants') return <WithSiteMenu><WithScanChooser><LazyRoute><><PlantLibrary /><SiteFooter /></></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/propagation') return <WithSiteMenu><WithScanChooser><LazyRoute><><PropagationLibrary /><SiteFooter /></></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path.startsWith('/propagation/')) return (
    <WithSiteMenu>
      <WithScanChooser>
        <LazyRoute>
          <>
            <Routes>
              <Route path="/propagation/:id" element={<PropagationGuidePage />} />
            </Routes>
            <SiteFooter />
          </>
        </LazyRoute>
      </WithScanChooser>
    </WithSiteMenu>
  );
  if (publicPlantGuide) return (
    <WithSiteMenu>
      <WithScanChooser>
        <LazyRoute>
          <>
            <Routes>
              <Route path="/plants/:id" element={<PublicPlantGuide />} />
            </Routes>
            <PlantSafetyNoticeLite />
            <RelatedPlantLinks />
            <PlantCorrectionWidgetLite />
            <SiteFooter />
          </>
        </LazyRoute>
      </WithScanChooser>
    </WithSiteMenu>
  );
  if (path === '/pricing') return <WithSiteMenu><WithScanChooser><LazyRoute><PricingPage /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/account') return <WithSiteMenu><WithScanChooser><LazyRoute><AccountPageV2 /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/privacy') return <WithSiteMenu><WithScanChooser><LazyRoute><PrivacyPage /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/terms') return <WithSiteMenu><WithScanChooser><LazyRoute><TermsPage /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/community-guidelines') return <WithSiteMenu><WithScanChooser><LazyRoute><CommunityGuidelinesPage /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/admin') return <WithSiteMenu><WithScanChooser><LazyRoute><AdminPage /></LazyRoute></WithScanChooser></WithSiteMenu>;
  if (path === '/my-plants') return <WithSiteMenu><WithScanChooser><LazyRoute><MyPlantsProductionV2 /></LazyRoute></WithScanChooser></WithSiteMenu>;

  const plantGuide = path.startsWith('/my-plants/');

  return (
    <WithSiteMenu>
      <App />
      {path === '/' && <HomeEnhancements />}
      {plantGuide && <PlantSafetyNoticeLite />}
      <PlantCorrectionWidgetLite />
      <Link className="community-floating-link" to="/community">
        <Flower2 size={18} /> Community
      </Link>
      <SiteFooter />
      <ScanSourceChooser />
    </WithSiteMenu>
  );
}
